import Bugsnag, { Client as BugsnagClient } from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import React, { Fragment } from "react";
import { Query } from "react-query";
import { Mutation } from "react-query/types/core/mutation";

import { appConfig } from "@/modules/app/appConfig";

const bugsnag =
  // eslint-disable-next-line no-restricted-globals
  process.env.NODE_ENV !== "test" && appConfig.bugsnag.apiKey
    ? Bugsnag.start({
        apiKey: appConfig.bugsnag.apiKey,
        plugins: [new BugsnagPluginReact()],
        appVersion: appConfig.release,
        releaseStage: appConfig.environment,
        redactedKeys: [/password/i, /passphrase/i, /token/i, /email/i],
      })
    : undefined;

export const ErrorBoundary =
  bugsnag?.getPlugin("react")?.createErrorBoundary(React) ?? Fragment;

export const notifyBugsnag: BugsnagClient["notify"] = bugsnag
  ? bugsnag.notify.bind(bugsnag)
  : () => undefined;

export async function notifyBugsnagQueryError(error: unknown, query: Query) {
  if (await checkIsQueryErrorHandled(error)) return;

  const key = Array.isArray(query.queryKey) ? query.queryKey : [query.queryKey];
  const name = key[0];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  notifyBugsnag(error as any, (event) => {
    event.addMetadata("query", { name, key });
  });
}

export async function notifyBugsnagMutationError(
  error: unknown,
  variables: unknown,
  _context: unknown,
  mutation?: Mutation<unknown, unknown, unknown, unknown>
) {
  if (await checkIsQueryErrorHandled(error)) return;

  const maybeKey = mutation?.options.mutationKey;
  const key = Array.isArray(maybeKey) ? maybeKey : [maybeKey];
  const name = key[0];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  notifyBugsnag(error as any, (event) => {
    event.addMetadata("mutation", { name, key, variables });
  });
}

// HACK: workaround for react-query global onError handler being called
// for all errors and before any local onError callbacks that might handle
// the error.
//
// The local onError callback can use markBugsnagQueryErrorHandled to mark an
// error as handled. The global callback delays itself with setTimeout(0)
// and checks if the error was handled and if so, ignores the error.

const handledSymbol = Symbol("QueryErrorHandled");
type HandledError = { [handledSymbol]?: boolean };

export function markQueryErrorHandled(error: unknown) {
  if (typeof error === "object" && error) {
    (error as HandledError)[handledSymbol] = true;
  }
}

async function checkIsQueryErrorHandled(error: unknown) {
  // wait for next tick
  await new Promise((resolve) => setTimeout(resolve, 0));

  return Boolean(
    typeof error === "object" && error && (error as HandledError)[handledSymbol]
  );
}
